import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { Switch } from '@/components/ui/switch';
import { useAppStatusActions } from '@/db/useAppStatusActions';
import { appStatusLiveState, easUpdateEditDocState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { zodResolver } from '@hookform/resolvers/zod';
import type { AppStatusEasUpdate, Timestamp } from '@rock/types';
import { type FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { AppStatusGuidanceCard, LiveSince } from './AppStatusGuidanceCard';
import { AppStatusStagingToolbar } from './AppStatusStagingToolbar';

const FormSchema = z.object({
    enabled: z.boolean(),
});

type FormValues = z.infer<typeof FormSchema>;

function toDoc(values: FormValues): AppStatusEasUpdate {
    return {
        object: 'app-status-eas-update',
        schema: 1,
        id: 'eas-update',
        enabled: values.enabled,
        updatedAt: new Date().toISOString() as Timestamp,
    };
}

export const OtaUpdatePage: FC = () => {
    const editDoc = useAsyncAtomValue(easUpdateEditDocState, undefined);
    const live = useAsyncAtomValue(appStatusLiveState('eas-update'), undefined);
    const { busy, saveDraft } = useAppStatusActions('eas-update');

    const form = useForm<FormValues, unknown, FormValues>({
        resolver: zodResolver(FormSchema),
        defaultValues: { enabled: false },
    });

    useEffect(() => {
        if (editDoc && !form.formState.isDirty) {
            form.reset({ enabled: !!editDoc.enabled });
        }
    }, [editDoc, form]);

    const onSubmit = async (values: FormValues) => {
        await saveDraft(toDoc(values));
        form.reset(values);
    };

    return (
        <div className="p-6 max-w-3xl space-y-4">
            <AppStatusStagingToolbar kind="eas-update" />
            <AppStatusGuidanceCard title="Forced OTA Update">
                <p>
                    Makes long-running app sessions apply the latest EAS (over-the-air JS) update without waiting for
                    the user to restart. Apps already download new bundles in the background (every 5 minutes and on
                    each foreground) and always apply them at cold start — normally the bundle just waits for the next
                    launch. With this ON, an app that has a downloaded update also applies it when the user returns
                    after being away for at least 30 seconds, so the reload feels like a fresh launch and never
                    interrupts someone mid-task.
                </p>
                <p>
                    Use it when a shipped JS bug needs to reach everyone fast (paired with publishing the actual EAS
                    update). It's silent — no prompt, no blocked screen. For native-version enforcement use Required
                    Upgrade instead. Turn it back OFF once the fleet is updated.
                </p>
            </AppStatusGuidanceCard>

            <Card>
                <CardHeader>
                    <CardTitle>Forced Apply</CardTitle>
                </CardHeader>
                <CardContent>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                            <FormField
                                control={form.control}
                                name="enabled"
                                render={({ field }) => (
                                    <FormItem className="flex items-center gap-3">
                                        <FormControl>
                                            <Switch checked={field.value} onCheckedChange={field.onChange} />
                                        </FormControl>
                                        <FormLabel>Force downloaded updates to apply on return-to-foreground</FormLabel>
                                    </FormItem>
                                )}
                            />
                            <div className="flex items-center gap-4">
                                <Button type="submit" disabled={form.formState.isSubmitting || busy}>
                                    {form.formState.isSubmitting ? 'Saving…' : 'Save Draft'}
                                </Button>
                                <LiveSince live={live} />
                            </div>
                        </form>
                    </Form>
                </CardContent>
            </Card>
        </div>
    );
};
